import {
  customerAccessTransfer,
  evseSelfOnboardingDetails,
} from "src/api/smartChargingClients";
import { onboardingStart } from "src/assets";
import { useTranslation } from "react-i18next";
import Text from "src/components/Displays/Text";
import { Box, CardMedia, Stack } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import PrimaryButton from "src/components/Shared/PrimaryButton";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import { ROUTES, SMART_CHARGING_ROUTES } from "src/consts/routes";
import { MicroFrontendDetails, SESSION_KEYS } from "src/consts/session";

import { EvseSelfOnboardingDetails } from "src/models/smartChargingClientsModels";

const Start = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation("translation");
  const transferId = searchParams.get("tid") || "";

  const [isLoading, setIsLoading] = useState(true);

  const [, setCustomerAccess] = useSessionStorage<string>(
    SESSION_KEYS.MFE_CUSTOMER_ACCESS,
  );
  const [, setMicroFrontendDetails] = useSessionStorage<MicroFrontendDetails>(
    SESSION_KEYS.MICRO_FRONTEND_DETAILS,
  );
  const [getCustomerAccess] = useSessionStorage<string>(
    SESSION_KEYS.MFE_CUSTOMER_ACCESS,
  );
  const [, setEvseSelfOnboardingData] =
    useSessionStorage<EvseSelfOnboardingDetails>(
      SESSION_KEYS.EVSE_SELF_ONBOARDING_DETAILS,
    );

  const getCustomerAccessTransfer = useCallback(async () => {
    setIsLoading(true);
    try {
      const sessionTransferId = getCustomerAccess();

      if (sessionTransferId) {
        const evseData = await evseSelfOnboardingDetails({
          xIsonCustomerToken: sessionTransferId,
        });
        setEvseSelfOnboardingData(evseData);

        setIsLoading(false);
        return;
      }

      const response = await customerAccessTransfer({
        payload: {
          tid: transferId,
        },
      });

      setCustomerAccess(response.access);
      setMicroFrontendDetails(response.microFrontendDetails);

      const evseData = await evseSelfOnboardingDetails({
        xIsonCustomerToken: response.access,
      });
      setEvseSelfOnboardingData(evseData);

      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      navigate(
        `${ROUTES.ERROR}?msgId=smart_charging_onboarding_transfer_error`,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getCustomerAccessTransfer();
  }, [getCustomerAccessTransfer]);

  const handleNextClick = () => {
    navigate(`${SMART_CHARGING_ROUTES.INTRO}?tid=${transferId}`);
  };

  return (
    <Stack gap={5}>
      <Text
        text={t("page.wallboxOnboardingStart.pageTitle")}
        sx={(theme) => ({
          fontWeight: 500,
          fontSize: "32px",
          lineHeight: "40px",
          fontFamily: "officina",
          color: theme.palette.mainFont.primary,
        })}
      />

      <Stack alignItems={"center"}>
        <Box height="120px" width="120px">
          <CardMedia
            component="img"
            alt="start onboarding"
            image={onboardingStart}
            sx={{ objectFit: "contain" }}
          />
        </Box>
      </Stack>

      <Stack gap={1}>
        <Text
          text={t("page.wallboxOnboardingStart.pageSubTitle")}
          sx={(theme) => ({
            fontSize: "14px",
            fontWeight: "400",
            lineHeight: "20px",
            fontFamily: "Inter",
            color: theme.palette.mainFont.primary,
          })}
        />

        <Stack paddingLeft={7} gap={1}>
          <Text
            text={<li>{t("page.wallboxOnboardingStart.point1")}</li>}
            sx={(theme) => ({
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "20px",
              fontFamily: "Inter",
              color: theme.palette.mainFont.primary,
            })}
          />
          <Text
            text={<li>{t("page.wallboxOnboardingStart.point2")}</li>}
            sx={(theme) => ({
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "20px",
              fontFamily: "Inter",
              color: theme.palette.mainFont.primary,
            })}
          />
        </Stack>
      </Stack>

      <Box>
        <PrimaryButton
          isLoading={isLoading}
          onClick={handleNextClick}
          text={t("page.wallboxOnboardingStart.nextBtnText")}
        />
      </Box>
    </Stack>
  );
};

export default Start;
